/**
 * AdminRail (#1528) — the persistent vertical nav for the Global Admin
 * tier. Replaces the click-in card grid as the primary way to move
 * between admin sections: every section is always one click away,
 * grouped under headers ordered by how often an operator reaches for
 * them.
 *
 * Renders straight from the single `ADMIN_SECTIONS` + `ADMIN_SECTION_GROUPS`
 * registry in `sections.ts` — adding / regrouping a section there is the
 * only edit needed. A parent section (AI Models) links to its landing
 * path; its children surface as the in-pane sub-tab strip on the section
 * itself, NOT as rail sub-items (keeps the rail flat + scannable).
 *
 * Active styling + sidebar tokens are shared with the workspace
 * `Sidebar` (`selectedSidebarClasses`, `bg-sidebar`) so the two left
 * rails read as the same design language. `unbuilt` sections render
 * dimmed + non-interactive, same status semantics the grid used.
 */
import { NavLink } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { selectedSidebarClasses } from '@/lib/styles'
import { ADMIN_SECTIONS, ADMIN_SECTION_GROUPS, type AdminSection } from './sections'

export function AdminRail() {
  return (
    <aside className="fixed left-0 top-14 z-40 h-[calc(100dvh-3.5rem)] w-[248px] overflow-y-auto border-r bg-sidebar">
      <nav className="flex flex-col p-3" aria-label="Global Admin sections">
        {/* Title doubles as the link back to the /admin overview grid. */}
        <NavLink
          to="/admin"
          end
          className={({ isActive }) =>
            cn(
              'mb-3 rounded-md px-2 py-1.5 text-sm font-semibold transition-colors',
              isActive ? selectedSidebarClasses : 'text-sidebar-foreground hover:bg-sidebar-accent',
            )
          }
        >
          Global Admin
        </NavLink>

        {ADMIN_SECTION_GROUPS.map((group) => {
          const items = ADMIN_SECTIONS.filter((s) => s.group === group.id)
          if (items.length === 0) return null
          return (
            <div key={group.id} className="mb-3">
              <div
                className="px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/50"
                title={group.sub}
              >
                {group.label}
              </div>
              {items.map((s) => (
                <RailItem key={s.path} section={s} />
              ))}
            </div>
          )
        })}
      </nav>
    </aside>
  )
}

function RailItem({ section }: { section: AdminSection }) {
  const Icon = section.icon

  if (section.status === 'unbuilt') {
    return (
      <div
        className="flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-sidebar-foreground/40"
        aria-disabled="true"
        title="Not available yet"
      >
        <Icon className={cn('h-4 w-4 shrink-0 opacity-50', section.iconColor)} />
        <span className="truncate">{section.label}</span>
        <span className="ml-auto text-[9px] uppercase tracking-wider">not yet</span>
      </div>
    )
  }

  return (
    <NavLink
      to={section.path}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors',
          isActive
            ? selectedSidebarClasses
            : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        )
      }
    >
      {/* Per-section brand color stays on in both states — matches the
          Sidebar's border-style selection (no fill inversion to fight). */}
      <Icon className={cn('h-4 w-4 shrink-0', section.iconColor)} />
      <span className="truncate">{section.label}</span>
    </NavLink>
  )
}
